<template>
  <FreePageLayout>
    <template #header>
      <FreeHeader class="p-4" is-centered>
        <template #leftAction>
          <FreeBackButton @click="router.back" />
        </template>
        <template #default>
          <h2
            class="text-text-neutral-primary text-center text-lg font-semibold"
          >
            {{ $t('passengersData.title') }}
          </h2>
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <div class="flex grow flex-col pb-6">
        <div v-if="isLoading" class="fixed left-0 top-0 z-50">
          <FullScreenLoader />
        </div>
        <FlightDetails
          v-if="dict"
          class="-mx-4"
          :dict="dict"
          :flights="bookingStore.mappedFlights"
          :city-mismatches="offer?.$meta.city_mismatch"
          :count-passengers="passengers.keys.value.length"
          :is-youth="isYouth"
          :search-query="offer?.$meta?.search_query"
          :is-multimodal="isMultimodalOffer"
        />
        <section class="mb-24 grow space-y-6">
          <PassengersSection
            v-if="passengers.state.value"
            :is-domestic="offer.$meta.is_domestic"
            :schema="passengersSchema.passengers.schema"
            :passenger-keys="passengers.keys.value"
            :passengers="passengers.state.value"
            :provider-class="offer?.$meta.provider_class"
            :departure-date="
              bookingStore.mappedFlights[0]?.segments?.[0]?.dep?.at
            "
            @onPassengerUpdate="onPassengersUpdate"
          />
          <div class="space-y-4">
            <p class="text-text-neutral-tertiary mb-4 text-xs font-medium">
              {{ $t('contacts.contactsData') }}
            </p>
            <ContactsForm />
          </div>
        </section>
        <FreeAlert
          ref="invalidPassengersAlertRef"
          class="sticky bottom-28 w-full"
          :duration="8000"
        >
          <template #icon>
            <img
              src="@/assets/images/red-warning.svg"
              width="24"
              height="24"
              alt=""
            />
          </template>
          <template #default>
            <p class="text-text-neutral-primary">
              {{ errorMessage }}
            </p>
          </template>
        </FreeAlert>
        <PriceAndButtonCard
          source-page="passengersData"
          :price-amount="Number(bookingStore.state?.ticketPrice)"
          @onButtonAction="onSubmit"
        />
        <SpecialSymbolsWarningModal :name="specialSymbolsWarningModalName" />
        <PassengerSaveErrorModal :name="passengerSaveErrorModalName" />
      </div>
    </template>
  </FreePageLayout>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useI18n } from 'vue-i18n';
  import { useRouter, useRoute } from 'vue-router';
  import { isEqual } from 'lodash-es';
  import { useVuelidate } from '@vuelidate/core';
  import { createStorage } from '@aviata/storage';
  import { usePassengers } from '@aviata/booking-composable';
  import {
    createPassengersData,
    type Passenger,
    type PassengerKey,
    type PassengersData,
  } from '@aviata/booking-data';
  import {
    FreeAlert,
    FreeBackButton,
    FreeHeader,
    FreePageLayout,
    FullScreenLoader,
  } from 'freedom-shared';
  import {
    controllerModel,
    providerClassLib,
    hashCodeLib,
    ticketDetails,
  } from 'shared';
  import type { Airflow } from '@aviata/platform-types';
  import { useQueryClient } from '@tanstack/vue-query';

  import {
    passengersFormLib,
    PassengersSection,
    SpecialSymbolsWarningModal,
    PassengerSaveErrorModal,
    checkIfPassengerHasRequiredDocumentFields,
    type PassengerWithDocType,
  } from '@/widgets/passengers-form';
  import { ContactsForm, contactsFormModel } from '@/widgets/contacts-form';
  import { ancillaryBaggageModel } from '@/widgets/ancillary-baggage';

  import { bookingStateModel } from '@/features/booking-state';
  import {
    useSavePassengers,
    savePassengersLib,
  } from '@/features/save-passengers';
  import { PriceAndButtonCard } from '@/features/price-and-button-card';

  import { sentryModel } from '@/shared/model';
  import { analyticsEventsLib, bloomreachLib } from '@/shared/lib';
  import type { amplitudeEventsConfigTypes } from '@/shared/config';

  import { FlightDetails } from './ui';

  type UpdatedPassengerPayload = {
    key: PassengerKey;
    passenger: Passenger;
  };

  const props = defineProps<{
    offerId: string;
  }>();

  const router = useRouter();
  const route = useRoute();
  const source = route.params
    .source as amplitudeEventsConfigTypes.DetailsPageOpenSource;
  const { t } = useI18n();

  const bottomSheet = controllerModel.useBottomSheet();
  const modal = controllerModel.useModal();
  const specialSymbolsWarningModalName = 'special-symbols-warning-modal';
  const passengerSaveErrorModalName = 'passenger-save-error-modal';

  const passengers = usePassengers();
  const passengersData = createPassengersData(
    createStorage(sessionStorage),
    'free-avia:passengers',
  );

  const validations = useVuelidate();
  const invalidPassengersAlertRef = ref<InstanceType<typeof FreeAlert>>();
  const errorMessage = ref('');
  const queryClient = useQueryClient();

  const bookingStore = bookingStateModel.useBookingStore();
  const contactsStore = contactsFormModel.useContactsStore();
  const ancillariesStore = ancillaryBaggageModel.useAncillariesStore();
  const { offer, dict, passengersSchema } = storeToRefs(bookingStore);
  const searchParams = hashCodeLib.getSearchParams(
    offer.value.$meta.search_query as string,
  );
  const { isYouth } = searchParams;
  const { isLoading, savePassengers } = useSavePassengers();

  const isMultimodalOffer = computed(
    () =>
      !!offer.value &&
      ticketDetails.lib.getOfferTransportKind(offer.value) !== 'air',
  );

  const onPassengersUpdate = (
    updatedPassengerPayload: UpdatedPassengerPayload,
  ) => {
    passengers.onUpdate(updatedPassengerPayload);
    if (passengers.state.value) {
      passengersData.save(passengers.state.value);
    }
  };

  const handleInitialsWithSpecialSymbolsSubmit = () => {
    const updatedPassengers =
      passengersFormLib.removeSpecialSymbolsFromInitials(
        passengers.state.value,
      );
    Object.entries(updatedPassengers).forEach(([key, passenger]) => {
      onPassengersUpdate({
        key: key as PassengerKey,
        passenger: passenger as Passenger,
      });
    });
    modal.show(specialSymbolsWarningModalName);
    analyticsEventsLib.trySubmitPassengersData({
      hasSpecialSymbolsInInitials: true,
      source,
      providerClass: bookingStore.offer.$meta.provider_class,
    });
  };

  const continueSubmit = () => {
    bookingStore.update({
      passengers: passengers.state.value,
      contacts: {
        ...contactsStore.state,
        /** @see {contactsStore.state} info about removing first two symbols */
        phone: contactsStore.state.phone.slice(2),
      },
    });

    bloomreachLib.update({
      email: contactsStore.state.email,
      isSubscribed: true,
    });

    const currentPassengersPayload =
      savePassengersLib.convertLocalPassengersToPassengersPayload(
        passengers.state.value,
        bookingStore.offer.$meta.provider_class,
      );

    if (
      isEqual(
        { offerId: props.offerId, passengers: currentPassengersPayload },
        bookingStore.passengersSavePayload,
      )
    ) {
      router.push({
        name: 'ProductsPage',
        params: { source },
      });
      return;
    }

    ancillariesStore.clearState();
    bookingStore.update({ seatMap: null });

    savePassengers(props.offerId, currentPassengersPayload)
      .then((res: Airflow.Booking.Passenger) => {
        bookingStore.setPassengersSaveResponse(res);
      })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ['products', props.offerId],
        });
      })
      .then(() => {
        bookingStore.setPassengersSavePayload({
          offerId: props.offerId,
          passengers: currentPassengersPayload,
        });
        bookingStore.updateMedicalInsuranceProducts([]);
        bookingStore.nullifyFlightDelayProducts();
      })
      .then(() => {
        router.push({
          name: 'ProductsPage',
          params: { source },
        });
      })
      .catch(err => {
        const statusCode = err.response?.status;

        // 500 code errors
        // ничего страшного что не сохранились пассажиры, просто переходим на страницу продуктов
        if (statusCode && statusCode >= 500) {
          sentryModel.captureException(err);
          router.push({
            name: 'ProductsPage',
            params: { source },
          });
          return;
        }

        // 400 code errors
        if (statusCode && statusCode >= 400 && statusCode < 500) {
          const errorMessage =
            err.response?.data?.errors?.[0]?.message ||
            t('passengersData.saveError.description');

          modal.show(passengerSaveErrorModalName, {
            title: t('passengersData.saveError.title'),
            message: errorMessage,
          });
          return;
        }

        sentryModel.captureException(err);
        errorMessage.value = t('booking.errors.default.title');
        invalidPassengersAlertRef.value?.show();
      });
  };

  const onSubmit = async () => {
    validations.value.$touch();

    // Явно вызываем валидацию для всех пассажиров
    await validations.value.$validate();

    if (validations.value.$error) {
      analyticsEventsLib.trySubmitPassengersData({
        validationErrors: validations.value.$errors,
        source,
        providerClass: bookingStore.offer.$meta.provider_class,
      });
      errorMessage.value = t(
        'passengersData.invalidPassengersData',
        passengers.keys.value.length,
      );
      invalidPassengersAlertRef.value?.show();
      return;
    }

    if (!passengers.state.value) {
      return;
    }

    const hasSpecialSymbolsInInitials =
      passengersFormLib.checkSpecialSymbolsInInitials(passengers.state.value);

    if (hasSpecialSymbolsInInitials) {
      handleInitialsWithSpecialSymbolsSubmit();
      return;
    }

    // Проверяем обязательные поля документа для FunSun
    if (providerClassLib.isFunSun(bookingStore.offer.$meta.provider_class)) {
      const passengersArray = Object.values(
        passengers.state.value,
      ) as PassengerWithDocType[];
      const passengerMissingData = passengersArray.find(
        pax => !checkIfPassengerHasRequiredDocumentFields(pax),
      );

      if (passengerMissingData) {
        errorMessage.value = t('passengersData.missingDocumentData');
        invalidPassengersAlertRef.value?.show();
        return;
      }
    }

    analyticsEventsLib.trySubmitPassengersData({
      hasSpecialSymbolsInInitials,
      source,
      providerClass: bookingStore.offer.$meta.provider_class,
    });

    continueSubmit();
  };

  onMounted(() => {
    analyticsEventsLib.openPassengersDataPage();

    if (passengersSchema.value) {
      const passengersKeys = Object.keys(
        passengersSchema.value.passengers.schema,
      ) as PassengerKey[];

      const { passengers: initialPassengers, passengersKeys: sortedKeys } =
        passengersData.init(passengersKeys);

      passengers.init({
        keys: sortedKeys,
        initialState: initialPassengers,
        schema: passengersSchema.value.passengers.schema,
      });
    } else {
      sentryModel.captureException(
        sentryModel.getReadableError(
          sentryModel.createCustomErrorText(
            sentryModel.errorPrefix.DEFAULT,
            'NO_PASSENGERS_SCHEMA',
          ),
          new Error('Passengers schema for passengers form validation is null'),
        ),
      );
    }
  });
</script>
